import axios from 'axios'
import { router, usePage } from '@inertiajs/vue3'
import { route } from 'ziggy-js'
import { PageProps } from '@/Models/Inertia'

/**
 * Web-heartbeat voor de monitor actieve gebruikers (GH #6960).
 *
 * Wie lang in één formulier typt, doet minutenlang geen request en zou uit de
 * telling "actief in de laatste 60s" vallen. Deze heartbeat vult dat gat, maar
 * enkel wanneer de gebruiker echt bezig is:
 * - de tab is zichtbaar;
 * - er was interactie in de laatste 60s (een open tab op de achtergrond telt niet);
 * - de server heeft de laatste 45s geen request van deze gebruiker geteld.
 *
 * Dat laatste weten we uit de response-header `X-Presence-Recorded` die de
 * server zet zodra hij een request telt (navigatie of heartbeat). Op Inertia-
 * events kunnen we niet vertrouwen: een geprefetchte pagina openen doet geen
 * request, en de server telt niet elke navigatie (throttle). De tijd staat in
 * localStorage, zodat meerdere tabs samen hooguit één ping per 45s sturen —
 * net als de server, die de throttle per sessie bijhoudt.
 *
 * Bewust goedkoop: één timer die elke 5s enkel timestamps vergelijkt, en
 * passive listeners die enkel een getal bijwerken.
 */

const PING_AFTER_MS = 45_000
const INTERACTION_WINDOW_MS = 60_000
const TICK_MS = 5_000
const STORAGE_KEY = 'nn-presence-recorded-at'
const RECORDED_HEADER = 'x-presence-recorded'
const PERMISSIONS_VERSION_HEADER = 'x-permissions-version'
// Antwoord van presence.permissions: enkel de props die van de permissieset
// afhangen. De rest van de pagina (en een half ingevuld formulier) blijft staan.
interface PermissionPropsResponse {
    menus: unknown
    profileMenus: unknown
    jsPermissions: string
    permissions_version: number | null
    session_entity_id: number | null
}
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'scroll', 'wheel', 'touchstart'] as const

// started: timer en listeners lopen. initialized: de eerste start sinds deze
// paginalading is gebeurd. halted: de server weigerde de heartbeat (sessie weg,
// CSRF ongeldig of niet-geverifieerd account); dan niet meer herstarten tot een
// nieuwe login of een volledige paginalading.
let started = false
let initialized = false
let halted = false
// Of de vorige pagina een ingelogde gebruiker had; null = nog niets gezien.
let hadUser: boolean | null = null
let timer: ReturnType<typeof setInterval> | null = null
let lastInteractionAt = 0
let lastPingAt = 0
let inFlight = false
let permissionsReloading = false
// Serverversie waarvoor we al herlaadden: na één reload niet bij elke ping opnieuw.
let lastReloadedVersion: string | null = null
// Terugval wanneer localStorage niet beschikbaar is (private mode, quota).
let memoryRecordedAt = 0

const readRecordedAt = (): number => {
    try {
        return Number(window.localStorage.getItem(STORAGE_KEY) ?? 0) || memoryRecordedAt
    } catch {
        return memoryRecordedAt
    }
}

const markRecorded = (): void => {
    const now = Date.now()
    memoryRecordedAt = now
    try {
        window.localStorage.setItem(STORAGE_KEY, String(now))
    } catch {
        // Geen opslag: de geheugenwaarde volstaat voor deze tab.
    }
}

/**
 * Permissies gewijzigd terwijl de tab openstond (GH #7052)?
 *
 * Menu en jsPermissions komen enkel mee met een Inertia-navigatie. Wie op één
 * pagina blijft, zag een afgelopen addon-proef (of een ingetrokken recht) dus
 * nooit verdwijnen. De heartbeat geeft de actuele permissions_version terug;
 * verschilt die van de versie waarmee deze pagina geladen werd, dan herladen we
 * enkel de permissie-props. De route-gate controleert elke request al live, dit
 * gaat enkel over wat de gebruiker ziet.
 */
const syncPermissionsVersion = (headerValue: unknown): void => {
    if (permissionsReloading || typeof headerValue !== 'string') {
        return
    }

    // Header = "{entityId}:{versie}" (HandleInertiaRequests::permissionsVersionHeader).
    const [entityPart, versionPart] = headerValue.split(':')
    const serverEntityId = Number(entityPart)
    const serverVersion = Number(versionPart)
    const props = usePage<PageProps>().props
    const pageVersion = props?.permissions_version

    if (
        !Number.isFinite(serverEntityId) ||
        !Number.isFinite(serverVersion) ||
        pageVersion === undefined ||
        pageVersion === null
    ) {
        return
    }

    // Andere sessie-entity dan waarmee deze pagina geladen werd: de gebruiker wisselde
    // in een andere tab van bedrijf of startte een impersonatie. Dan bewust niets doen
    // (beslissing 09/10/2026, review PR #7054): een reload zou deze URL onder de nieuwe
    // context openen, met een foutmodal of een verloren formulier als gevolg.
    if (serverEntityId !== Number(props.session_entity_id)) {
        return
    }

    if (serverVersion === Number(pageVersion) || headerValue === lastReloadedVersion) {
        return
    }

    permissionsReloading = true
    lastReloadedVersion = headerValue
    // Eigen JSON-endpoint i.p.v. router.reload({ only }): die draaide de controller
    // van de huidige URL volledig opnieuw (modal-URL's, GET's met neveneffecten, 405
    // op een pagina na een POST). Hier enkel de gedeelde permissie-props.
    axios
        .get<PermissionPropsResponse>(route('presence.permissions'))
        .then(({ data }) => {
            // Intussen in een andere tab van entity gewisseld: niets doen.
            if (data.session_entity_id !== usePage<PageProps>().props?.session_entity_id) {
                return
            }
            router.replace({
                props: (current) => ({
                    ...current,
                    menus: data.menus,
                    profileMenus: data.profileMenus,
                    jsPermissions: data.jsPermissions,
                    permissions_version: data.permissions_version,
                }),
                preserveState: true,
                preserveScroll: true,
            })
        })
        .catch((error: unknown) => {
            // Enkel tijdelijke fouten opnieuw proberen bij de volgende heartbeat: een 429
            // van de gedeelde throttle (meerdere tabs tegelijk) of een netwerkfout. Bij
            // elke andere fout (bv. een blijvende 500) bleef elke tab vroeger elke ±45 s
            // opnieuw ophalen, en die pogingen verbruikten de throttle van de gewone
            // heartbeat (review PR #7054). De versie geldt dan als afgehandeld; de
            // route-gate controleert elke request toch live.
            const response = (error as { response?: { status?: number } })?.response
            if (!response || response.status === 429) {
                lastReloadedVersion = null
            }
        })
        .finally(() => {
            permissionsReloading = false
        })
}

const onInteraction = (): void => {
    lastInteractionAt = Date.now()
}

const stop = (): void => {
    if (timer !== null) {
        clearInterval(timer)
        timer = null
    }
    INTERACTION_EVENTS.forEach((type) =>
        window.removeEventListener(type, onInteraction, { capture: true })
    )
    started = false
}

const currentRouteName = (): string | null => {
    try {
        return route().current() ?? null
    } catch {
        return null
    }
}

const tick = async (): Promise<void> => {
    const now = Date.now()

    if (
        inFlight ||
        document.visibilityState !== 'visible' ||
        now - lastInteractionAt > INTERACTION_WINDOW_MS ||
        now - readRecordedAt() < PING_AFTER_MS ||
        // Ook wanneer de server een ping niet telde (bv. een andere tab ging
        // net voor), niet meteen opnieuw proberen.
        now - lastPingAt < PING_AFTER_MS
    ) {
        return
    }

    // Uitgelogd (bv. na een Inertia-logout zonder herladen): niets meer sturen.
    if (!usePage<PageProps>().props?.auth?.user) {
        return
    }

    inFlight = true
    lastPingAt = now

    try {
        await axios.post(
            route('presence.heartbeat'),
            { route: currentRouteName() },
            // Achtergrondcall: nooit een permissie-/licentie-toast tonen.
            { silentPermissionErrors: true }
        )
    } catch (error: unknown) {
        // Sessie verlopen (401), CSRF-token ongeldig (419) of een account dat
        // zijn e-mail nog niet bevestigde (403/409: de route zit in de groep
        // 'verified'): stoppen én niet bij de volgende navigatie herstarten,
        // anders faalt elke tab om de 45s opnieuw. Enkel een nieuwe login of een
        // volledige paginalading heft dat op (zie syncPresenceHeartbeat).
        // Andere fouten (netwerk, 429, 5xx) stil negeren; de volgende ping
        // probeert opnieuw.
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status === 401 || status === 403 || status === 409 || status === 419) {
            halted = true
            stop()
        }
    } finally {
        inFlight = false
    }
}

const start = (): void => {
    if (started || halted) {
        return
    }
    started = true

    if (!initialized) {
        initialized = true

        // Elke response die de server als activiteit telde, ook gewone Inertia-
        // navigaties (Inertia gebruikt dezelfde axios-instance), schuift de
        // volgende ping op. Headers komen hier lowercase binnen. Eenmalig per
        // paginalading registreren: start() kan na een stop() opnieuw lopen.
        axios.interceptors.response.use((response) => {
            if (response?.headers?.[RECORDED_HEADER]) {
                markRecorded()
            }
            syncPermissionsVersion(response?.headers?.[PERMISSIONS_VERSION_HEADER])
            return response
        })

        // Enkel bij de eerste start: die request (de paginalading of de login-
        // redirect) telde de server al. Bij een herstart na uitloggen klopt dat
        // niet meer en zou de eerste ping onterecht 45s uitgesteld worden.
        markRecorded()
    }

    INTERACTION_EVENTS.forEach((type) =>
        window.addEventListener(type, onInteraction, { passive: true, capture: true })
    )

    timer = setInterval(() => {
        void tick()
    }, TICK_MS)
}

/**
 * Volgt de ingelogde gebruiker van de huidige pagina (initiële pagina en elke
 * Inertia-navigatie). Idempotent.
 *
 * Gaat de gebruiker van afwezig naar aanwezig, dan is er opnieuw ingelogd
 * zonder volledige paginalading: een eerdere stop na 401/403/409/419 geldt dan
 * niet meer. Zolang dezelfde gebruiker blijft, herstart een navigatie een
 * gestopte heartbeat bewust niet.
 */
export function syncPresenceHeartbeat(hasUser: boolean): void {
    if (typeof window === 'undefined') {
        return
    }
    if (hasUser && hadUser === false) {
        halted = false
    }
    hadUser = hasUser

    if (hasUser) {
        start()
    } else {
        stop()
    }
}
